/**
 * `useThreadActivity(context)` — combined "unread count" + "discussion exists"
 * signal for a single message thread (M-MSG, design-messaging-ux.md §3.2 —
 * "Diskussion vorhanden"-Indikator on `MessageContextButton`).
 *
 * `useUnreadCount(context)` alone cannot distinguish a fully-read thread
 * (0 unread, but a discussion happened) from a thread that has never been
 * written in (also 0 unread) — both report `0`. This hook fetches both
 * {@link getThreadUnreadCount} and {@link getThreadMessageCount} so the
 * consuming UI can render a "discussion exists, all read" state distinct from
 * "no discussion yet".
 *
 * v1 note: like {@link useUnreadCount}, this is a one-shot `count()` fetch
 * (no live subscription) that re-runs when the auth scope or the context
 * identity changes. Errors fail SAFE to `{ unread: 0, hasMessages: false }`
 * (never block the UI) — the underlying API calls already fail-soft to 0, so
 * this hook's own try/catch is a defense-in-depth belt-and-braces guard, not
 * the primary error path.
 *
 * @module useThreadActivity
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageContext } from '../types/messagingTypes';
import { getThreadUnreadCount, getThreadMessageCount } from '../api/messagesApi';
import logger from '../utils/logger';

/** Combined unread/activity signal for a single thread. */
export interface ThreadActivity {
  /** Unread message count in the thread (fails safe to 0). */
  unread: number;
  /** True iff the thread has ANY message ever posted (read or unread). */
  hasMessages: boolean;
}

const EMPTY_ACTIVITY: ThreadActivity = { unread: 0, hasMessages: false };

/**
 * Returns `{ unread, hasMessages }` for `context`. Fails safe to
 * `{ unread: 0, hasMessages: false }` when unauthenticated or on error.
 *
 * @param context - The thread context to inspect.
 * @returns The thread's unread count and whether any message exists at all.
 */
export function useThreadActivity(context: MessageContext): ThreadActivity {
  const { currentUser, effectiveScopeUid } = useAuth();
  const [activity, setActivity] = useState<ThreadActivity>(EMPTY_ACTIVITY);

  const ctxType = context.type;
  const ctxRefId = context.refId ?? null;

  useEffect(() => {
    if (!currentUser) {
      setActivity(EMPTY_ACTIVITY);
      return;
    }

    let cancelled = false;
    const readerUid = currentUser.uid;
    const scopeUid = effectiveScopeUid;
    const ctx: MessageContext = { type: ctxType, refId: ctxRefId };

    const run = async (): Promise<void> => {
      try {
        const [unread, total] = await Promise.all([
          getThreadUnreadCount(ctx, scopeUid, readerUid),
          getThreadMessageCount(ctx, scopeUid),
        ]);
        if (!cancelled) {
          setActivity({ unread, hasMessages: total > 0 });
        }
      } catch (err) {
        logger.warn(
          'useThreadActivity: fetch failed — failing safe to no activity',
          err instanceof Error ? err : new Error(String(err)),
        );
        if (!cancelled) {
          setActivity(EMPTY_ACTIVITY);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [currentUser, effectiveScopeUid, ctxType, ctxRefId]);

  return activity;
}
